<script setup lang="ts">
import { toasts, dismiss } from '../game/toast';
</script>

<template>
  <div class="th" aria-live="polite">
    <TransitionGroup name="th">
      <button
        v-for="t in toasts" :key="t.id"
        class="th-toast" :class="`th-${t.kind}`"
        :style="t.accent ? { '--th-rail': t.accent } : undefined"
        @click="dismiss(t.id)"
      >
        <img v-if="t.icon" :src="t.icon" alt="" class="th-icon" />
        <span class="th-msg">{{ t.message }}</span>
      </button>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.th {
  position: fixed;
  /* clears the home-indicator safe area on notched phones, where a bare
     22px sits right under (or behind) the gesture bar. */
  bottom: max(22px, calc(22px + env(safe-area-inset-bottom)));
  left: 50%;
  transform: translateX(-50%);
  z-index: 400;
  display: flex; flex-direction: column-reverse; align-items: center; gap: 8px;
  pointer-events: none;
  width: min(480px, calc(100vw - 32px));
}
/* a toast is a struck iron tag, not a banded strip: hewn shape on ::before
   (z:-1 in an isolated context — a positioned pseudo at z:0 would paint over
   the bare text), forged texture soft-lit into the face, the accent chased in
   along the bottom edge like an inlay. */
.th-toast {
  pointer-events: auto;
  position: relative;
  isolation: isolate;
  display: flex; align-items: center; gap: 10px;
  max-width: 100%;
  padding: 11px 20px 12px 15px;
  border: 0;
  border-radius: 0;
  cursor: pointer;
  text-align: left;
  filter: drop-shadow(0 10px 22px rgba(0, 0, 0, 0.65))
    drop-shadow(0 0 14px color-mix(in srgb, var(--th-rail, var(--color-gold)) 16%, transparent));
}
.th-toast::before {
  content: '';
  position: absolute; inset: 0; z-index: -1;
  clip-path: var(--clip-hewn);
  background:
    radial-gradient(90% 100% at 8% 50%, color-mix(in srgb, var(--th-rail, var(--color-gold)) 13%, transparent), transparent 60%),
    var(--tex-forge),
    linear-gradient(180deg, #1a2230, #0d1219 62%, #10151e);
  background-size: auto, 190px 190px, auto;
  background-blend-mode: normal, soft-light, normal;
  box-shadow:
    inset 0 1px 0 rgba(226, 236, 248, 0.10),
    inset 0 -2px 0 color-mix(in srgb, var(--th-rail, var(--color-gold)) 55%, transparent),
    inset 0 0 0 1px rgba(0, 0, 0, 0.55);
}
.th-success { --th-rail: var(--color-gold); }
.th-error { --th-rail: var(--color-error); }
.th-info { --th-rail: var(--color-purple-light); }
.th-icon {
  width: 28px; height: 28px; flex: none;
  object-fit: contain;
  border-radius: 0;
  filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.7))
    drop-shadow(0 0 6px color-mix(in srgb, var(--th-rail, var(--color-gold)) 30%, transparent));
}
.th-msg {
  font-family: var(--font-body, serif);
  font-size: 14.5px; line-height: 1.35;
  color: var(--color-text);
}
.th-error .th-msg { color: color-mix(in srgb, var(--color-error) 35%, var(--color-text)); }

.th-enter-active { transition: opacity 240ms ease, transform 240ms cubic-bezier(0.2, 1.1, 0.35, 1); }
.th-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.th-enter-from { opacity: 0; transform: translateY(14px) scale(0.97); }
.th-leave-to { opacity: 0; transform: translateY(6px); }
@media (prefers-reduced-motion: reduce) {
  .th-enter-active, .th-leave-active { transition: none; }
}
</style>
